/**
 * @file Editable object composables for form state management
 * @version 1.0.0
 * @since 2025-09-26
 */

import { validateSchemaValue } from './schemas.js';

/**
 * Creates an editable state manager for objects
 * @param {Function|import('vue').Ref|*} original - Getter function, Vue ref, or plain value to track
 * @param schema.schema
 * @param {object} schema - Optional JSON Schema for validation
 * @param schema.validate
 */
export function useEditableObject(
  original,
  { schema = null, validate = null } = {}
) {
  // Normalise to a getter so Vue.watch tracks the source reactively.
  // Accepting a getter or ref (instead of a plain value) lets callers create
  // this object once in setup() rather than inside a computed property,
  // which would leak watchers on every re-evaluation.
  const getOriginal =
    typeof original === 'function'
      ? original
      : Vue.isRef(original)
        ? () => original.value
        : () => original;

  const editable = Vue.ref(null);
  // Snapshot the original's JSON at the time of the last reset. isModified
  // compares only editable vs this snapshot — NOT against getOriginal() directly.
  // Tracking getOriginal() inside isModified would cause it to go spuriously
  // true when props.value changes but editable hasn't been reset yet, producing
  // "maximum recursive updates" as child editors emit stale values back up.
  const originalJSON = Vue.ref('null');

  const reset = () => {
    const json = JSON.stringify(getOriginal() ?? null);
    editable.value = JSON.parse(json) ?? undefined; // null → undefined (no value)
    originalJSON.value = json;
  };

  Vue.watch(getOriginal, reset, { immediate: true });

  const isModified = Vue.computed(
    () => JSON.stringify(editable.value) !== originalJSON.value
  );

  const isValid = Vue.computed(() => {
    return validationErrors.value.length === 0;
  });

  const validationErrors = Vue.computed(() => {
    const resolvedSchema = Vue.isRef(schema) ? schema.value : schema;
    let errors = [];
    if (resolvedSchema) {
      errors = errors.concat(
        validateSchemaValue(editable.value, resolvedSchema)
      );
    }
    if (validate) {
      errors = errors.concat(validate(editable.value));
    }
    return errors;
  });

  return Vue.reactive({
    editable,
    isModified,
    isValid,
    validationErrors,
    save: () => editable.value,
    reset,
  });
}
